import { Link, Navigate } from 'react-router-dom';
import { ArrowRight, PhoneCall } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';
import SEOHead from '../../components/SEOHead';
import GoogleReviews from '../../components/GoogleReviews';
import { PORTAL_LINKS, SITE_PHONE_DISPLAY, SITE_PHONE_TEL } from '../../constants/site';
import { COMMERCIAL_PAGES_BY_PATH, LEARN_POSTS_BY_SLUG } from '../../data/seoRetrofitData';
import { getImageForPath } from '../../data/siteImages';

export default function CommercialIntentPage({ pagePath }) {
  const page = COMMERCIAL_PAGES_BY_PATH[pagePath];
  if (!page) return <Navigate to="/" replace />;

  const breadcrumbs = page.path
    .split('/')
    .filter(Boolean)
    .map((part, index, arr) => ({
      name: part.replace(/-/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase()),
      path: `/${arr.slice(0, index + 1).join('/')}`,
    }));

  const relatedLearn = (page.relatedLearnSlugs || [])
    .map((slug) => LEARN_POSTS_BY_SLUG[slug])
    .filter(Boolean)
    .slice(0, 4);

  const heroImage = getImageForPath(page.path);
  const portalHref = page.path.startsWith('/business-loans') ? PORTAL_LINKS.BUSINESS : PORTAL_LINKS.COMMERCIAL;

  const customSchema = [];
  if (page.type === 'pillar' || page.type === 'subproduct') {
    customSchema.push({
      '@context': 'https://schema.org',
      '@type': 'FinancialService',
      name: 'Flash Funding',
      url: `https://www.flashfunding.loan${page.path}`,
      telephone: '+611800003527',
      areaServed: 'AU',
      description: page.description,
    });
  }

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid={`commercial-page-${page.path.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}>
      <SEOHead
        title={page.title}
        description={page.description}
        canonical={page.path}
        keywords={`${page.primaryKeyword}, asset finance australia, flash funding`}
        breadcrumbs={[{ name: 'Home', path: '/' }, ...breadcrumbs]}
        faqs={(page.faqs || []).map((faq) => ({ q: faq.q, a: faq.a }))}
        schema={customSchema}
      />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-10" data-testid="commercial-page-hero-section">
        <div className="grid grid-cols-1 xl:grid-cols-[1.25fr_0.9fr] gap-6 items-start">
          <div className="rounded-3xl border border-purple-200 bg-white p-6 shadow-xl shadow-purple-200/25 sm:p-8" data-testid="commercial-page-hero-copy-wrap">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#0F0A1A] leading-[1.05]" data-testid="commercial-page-h1">
              {page.h1.split(' | ')[0]}
            </h1>
            <p className="mt-4 text-sm text-[#4A4556] sm:text-base" data-testid="commercial-page-value-prop">{page.valueProp}</p>

            <div className="mt-5 aspect-[16/7] rounded-2xl overflow-hidden border border-purple-100" data-testid="commercial-page-hero-image-wrap">
              <img
                src={heroImage.url}
                alt={heroImage.alt}
                loading="lazy"
                className="h-full w-full object-cover"
                data-testid="commercial-page-hero-image"
              />
            </div>

            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="commercial-page-trust-strip">
              {page.trust.map((item) => (
                <TrustChip key={item.label} {...item} testId={`trust-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`} />
              ))}
            </div>

            <div className="mt-6 flex flex-wrap gap-3" data-testid="commercial-page-hero-ctas">
              <a href={portalHref} data-testid="commercial-page-primary-cta-link">
                <Button className="rounded-full bg-purple-600 px-7 py-6 text-xs font-bold uppercase tracking-[0.14em] text-white hover:bg-purple-700" data-testid="commercial-page-primary-cta">
                  Get my rate in 60 seconds
                  <ArrowRight size={14} className="ml-2" />
                </Button>
              </a>
              <a href={SITE_PHONE_TEL} data-testid="commercial-page-secondary-cta-link">
                <Button variant="outline" className="rounded-full border-purple-300 bg-purple-50 px-7 py-6 text-xs font-bold uppercase tracking-[0.14em] text-purple-700 hover:bg-purple-100" data-testid="commercial-page-secondary-cta">
                  <PhoneCall size={14} className="mr-2" />
                  Call {SITE_PHONE_DISPLAY}
                </Button>
              </a>
            </div>

          </div>

          <div className="rounded-3xl border border-purple-200 bg-white p-6 shadow-xl shadow-purple-200/25 sm:p-7" data-testid="commercial-page-hero-quote-form-wrap">
            <p className="text-xs uppercase tracking-[0.15em] text-purple-700" data-testid="commercial-page-portal-card-eyebrow">Secure Customer Portal</p>
            <h2 className="mt-2 text-2xl font-bold text-[#0F0A1A]" data-testid="commercial-page-portal-card-heading">Start your quote online</h2>
            <p className="mt-3 text-sm text-[#4A4556]" data-testid="commercial-page-portal-card-description">
              Complete a guided application and move straight to product-specific lender pathways.
            </p>
            <ul className="mt-4 space-y-2 text-sm text-[#4A4556]" data-testid="commercial-page-portal-card-bullets">
              <li>• Commercial finance pathways matched to your asset or cashflow needs</li>
              <li>• Clear next steps and secure document workflow</li>
              <li>• Fast follow-up from the Flash Funding team during business hours</li>
            </ul>
            <div className="mt-5 flex flex-col gap-3" data-testid="commercial-page-portal-card-actions">
              <a href={portalHref} data-testid="commercial-page-portal-card-primary-link">
                <Button className="h-11 w-full rounded-lg bg-purple-600 text-sm font-semibold text-white hover:bg-purple-700" data-testid="commercial-page-portal-card-primary-cta">
                  Continue in Portal <ArrowRight size={14} className="ml-2" />
                </Button>
              </a>
              <a href={SITE_PHONE_TEL} data-testid="commercial-page-portal-card-phone-link">
                <Button variant="outline" className="h-11 w-full rounded-lg border-purple-300 bg-purple-50 text-sm font-semibold text-purple-700 hover:bg-purple-100" data-testid="commercial-page-portal-card-phone-cta">
                  <PhoneCall size={14} className="mr-2" />
                  Call {SITE_PHONE_DISPLAY}
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12" data-testid="commercial-page-how-it-works-section">
        <h2 className="mb-6 text-3xl font-bold text-[#0F0A1A] sm:text-4xl">How it works</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {page.howItWorks.map((step, index) => (
            <article key={step} className="rounded-2xl border border-purple-200 bg-white p-5" data-testid={`commercial-page-step-card-${index + 1}`}>
              <span className="mb-3 inline-flex h-8 w-8 items-center justify-center rounded-full bg-purple-100 text-sm text-purple-700" data-testid={`commercial-page-step-number-${index + 1}`}>{index + 1}</span>
              <p className="text-sm text-[#4A4556]" data-testid={`commercial-page-step-text-${index + 1}`}>{step}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12" data-testid="commercial-page-what-we-finance-section">
        <h2 className="mb-6 text-3xl font-bold text-[#0F0A1A] sm:text-4xl">What we finance</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="commercial-page-finance-grid">
          {page.whatWeFinance.map((item) => (
            <Link key={item.path} to={item.path} className="rounded-2xl border border-purple-200 bg-white p-5 transition-[border-color,transform,box-shadow] hover:-translate-y-[1px] hover:border-purple-400 hover:shadow-lg hover:shadow-purple-200/30" data-testid={`commercial-page-finance-link-${item.path.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}>
              <h3 className="mb-1 text-lg font-semibold text-[#1B1431]">{item.label}</h3>
              <p className="text-xs text-[#625b78]">Explore {item.label.toLowerCase()} options</p>
            </Link>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12" data-testid="commercial-page-rates-section">
        <h2 className="mb-5 text-3xl font-bold text-[#0F0A1A] sm:text-4xl">Rates and fees</h2>
        <div className="overflow-hidden rounded-2xl border border-purple-200 bg-white">
          <div className="w-full" role="table" data-testid="commercial-page-rates-table">
            {page.ratesRows.map((row) => (
              <div key={row.label} role="row" className="grid gap-1 border-b border-purple-100 px-4 py-3 sm:grid-cols-[0.9fr_1.1fr] sm:gap-4">
                <div role="cell" className="text-sm text-[#4A4556]" data-testid={`commercial-page-rates-label-${row.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>{row.label}</div>
                <div role="cell" className="text-sm font-medium text-[#1B1431]" data-testid={`commercial-page-rates-value-${row.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>{row.value}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-6 mb-12" data-testid="commercial-page-eligibility-differentiators-section">
        <article className="rounded-2xl border border-purple-200 bg-white p-6" data-testid="commercial-page-eligibility-wrap">
          <h2 className="mb-4 text-2xl font-bold text-[#0F0A1A] sm:text-3xl">Eligibility</h2>
          <ul className="list-disc space-y-2 pl-5 text-sm text-[#4A4556]">
            {page.eligibility.map((point) => (
              <li key={point} data-testid={`commercial-page-eligibility-item-${point.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>{point}</li>
            ))}
          </ul>
        </article>

        <article className="rounded-2xl border border-purple-200 bg-white p-6" data-testid="commercial-page-why-wrap">
          <h2 className="mb-4 text-2xl font-bold text-[#0F0A1A] sm:text-3xl">Why Flash Funding</h2>
          <ul className="list-disc space-y-2 pl-5 text-sm text-[#4A4556]">
            {page.differentiators.map((point) => (
              <li key={point} data-testid={`commercial-page-why-item-${point.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>{point}</li>
            ))}
          </ul>
        </article>
      </section>

      {page.type === 'city' && page.localContent && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12" data-testid="commercial-page-city-local-content-section">
          <h2 className="mb-5 text-3xl font-bold text-[#0F0A1A] sm:text-4xl">How we work with {page.path.replace('/locations/', '').replace(/\b\w/g, (m) => m.toUpperCase())} businesses</h2>
          <div className="space-y-4 rounded-2xl border border-purple-200 bg-white p-6 text-sm leading-relaxed text-[#4A4556]" data-testid="commercial-page-city-local-content-wrap">
            {page.localContent.map((paragraph, index) => (
              <p key={index} data-testid={`commercial-page-city-local-paragraph-${index + 1}`}>{paragraph}</p>
            ))}
          </div>
        </section>
      )}

      <div className="mb-12" data-testid="commercial-page-reviews-section">
        <GoogleReviews variant="home" />
      </div>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12" data-testid="commercial-page-faq-section">
        <h2 className="mb-5 text-3xl font-bold text-[#0F0A1A] sm:text-4xl">FAQ</h2>
        <Accordion type="single" collapsible className="space-y-3">
          {(page.faqs || []).map((faq, index) => (
            <AccordionItem key={faq.q} value={`faq-${index}`} className="rounded-2xl border border-purple-200 bg-white px-5">
              <AccordionTrigger className="text-left text-[#1B1431]" data-testid={`commercial-page-faq-question-${index + 1}`}>{faq.q}</AccordionTrigger>
              <AccordionContent className="text-sm text-[#4A4556]" data-testid={`commercial-page-faq-answer-${index + 1}`}>{faq.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 xl:grid-cols-2 gap-6 mb-12" data-testid="commercial-page-related-section">
        <article className="rounded-2xl border border-purple-200 bg-white p-5" data-testid="commercial-page-related-pages-card">
          <h2 className="mb-4 text-2xl font-bold text-[#0F0A1A]">Related pages</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {[...(page.whatWeFinance || []), ...(page.relatedCities || []).map((city) => ({ label: city.replace('/locations/', '').replace(/-/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase()), path: city }))].slice(0, 8).map((item) => (
              <Link key={item.path} to={item.path} className="text-sm text-purple-700 hover:text-purple-800" data-testid={`commercial-page-related-link-${item.path.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}>
                {item.label}
              </Link>
            ))}
          </div>
        </article>

        <article className="rounded-2xl border border-purple-200 bg-white p-5" data-testid="commercial-page-related-learn-card">
          <h2 className="mb-4 text-2xl font-bold text-[#0F0A1A]">Related guides</h2>
          <div className="space-y-2">
            {relatedLearn.map((post) => (
              <Link key={post.slug} to={`/learn/${post.slug}`} className="block text-sm text-purple-700 hover:text-purple-800" data-testid={`commercial-page-related-learn-link-${post.slug}`}>
                {post.title}
              </Link>
            ))}
            <Link to="/calculators/asset-finance-calculator" className="block text-sm text-purple-700 hover:text-purple-800" data-testid="commercial-page-related-calculator-link">
              Asset Finance Calculator
            </Link>
          </div>
        </article>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="commercial-page-final-cta-section">
        <div className="rounded-3xl border border-purple-600/30 bg-purple-700 p-6 sm:p-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">Ready to compare lender options?</h2>
          <p className="text-sm sm:text-base text-white/85 mb-6">Start in our secure portal and follow the guided pathway for your product type.</p>
          <div className="flex flex-col gap-3 sm:flex-row" data-testid="commercial-page-final-cta-row">
            <a href={portalHref} data-testid="commercial-page-final-primary-link">
              <Button className="h-11 rounded-lg bg-white px-6 text-sm font-semibold text-purple-700 hover:bg-purple-50" data-testid="commercial-page-final-primary-cta">
                Continue in Portal <ArrowRight size={14} className="ml-2" />
              </Button>
            </a>
            <a href={SITE_PHONE_TEL} data-testid="commercial-page-final-secondary-link">
              <Button variant="outline" className="h-11 rounded-lg border-white/70 bg-transparent px-6 text-sm font-semibold text-white hover:bg-white/10" data-testid="commercial-page-final-secondary-cta">
                <PhoneCall size={14} className="mr-2" />
                Call {SITE_PHONE_DISPLAY}
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

function TrustChip({ label, value, path, testId }) {
  return (
    <article className="rounded-xl border border-purple-200 bg-[#faf9fd] p-3" data-testid={`commercial-page-${testId}`}>
      <p className="text-[10px] uppercase tracking-[0.12em] text-[#7B758A]">{label}</p>
      {path ? (
        <Link to={path} className="mt-1 block text-xs text-purple-700 hover:text-purple-800">{value}</Link>
      ) : (
        <p className="mt-1 text-xs text-[#1B1431]">{value}</p>
      )}
    </article>
  );
}
